import React, { Dispatch, SetStateAction, useState, useCallback, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { countryName, countryCode, countryList } from '../../libraries/country.library';
import { legendValid } from '../../utils/legend.data.util';
import { prefixLegend } from '../../scripts/name.legend.script';
import { useDropdownKeyboardNav } from '../../hooks/useDropdownKeyboardNav';

import AppLegend from './Legend';

export interface AppInputCountryProps {
  component?: string,
  options: {small: boolean, disabled: boolean, placeholder: string | null},
  country: {value: string, valid: boolean},
  setCountry: Dispatch<SetStateAction<{value: string, valid: boolean}>>
};

const AppInputCountry: React.FunctionComponent<AppInputCountryProps> = ({component, options, country, setCountry}) => {
  const {lang} = StateLanguage()

  const [search, setSearch] = useState<{text: string, list: string[]}>({text: '', list: []})
  const [isOpen, setIsOpen] = useState(false)
  const toggleRef = useRef<HTMLButtonElement>(null)

  const handleChangeSearch = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let text: string = event.target.value
    let list: string[] = []

    if (text.length > 0) {
      for (let c of countryList(lang.code)) {
        if (c.toLowerCase().indexOf(text.toLowerCase()) !== -1) {
          list.push(c)
        }
      }
    } else {
      list = countryList(lang.code)
    }

    setSearch({text: text, list: list})
  }

  const handleChangeCountry = useCallback((item: string) => {
    setCountry({value: item, valid: true})
    legendValid(`${prefixLegend(component)}legend-country-required`)
  }, [setCountry, component])

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow)
    if (nextShow) setSearch({text: '', list: countryList(lang.code)})
  }

  const handleClose = useCallback(() => {
    toggleRef.current?.click()
  }, [])

  const { highlightedIndex, handleKeyDown, getOptionId, activeDescendant, listboxId } =
    useDropdownKeyboardNav({
      itemCount: search.list.length,
      onSelect: (idx) => { handleChangeCountry(countryCode(lang.code, search.list[idx])); handleClose(); },
      isOpen,
      onClose: handleClose,
      idPrefix: 'country',
    })

  return (
    <>
      <Dropdown onToggle={handleToggle}>
        <Dropdown.Toggle ref={toggleRef} variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0" disabled={options.disabled}>
          <span className="selection">
            { options.small
              ?
              <span className="select2-selection select2-selection--single form-select-sm form-select">
                <span role="textbox">
                  {country.valid && country.value.length > 0 ? countryName(lang.code, country.value) : options.placeholder ?? lang.labels.selectOption}
                </span>
              </span>
              :
              <span className="select2-selection select2-selection--single form-select">
                <span className="select2-selection__rendered" role="textbox">
                  {country.valid && country.value.length > 0 ? countryName(lang.code, country.value) : options.placeholder ?? lang.labels.selectOption}
                </span>
              </span>
            }
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-search select2-search--dropdown">
              <input className="select2-search__field" type="text" autoComplete="off" name="country" role="combobox" aria-autocomplete="list" aria-controls={listboxId} aria-activedescendant={activeDescendant} aria-expanded={isOpen} value={search.text} onChange={handleChangeSearch} onKeyDown={handleKeyDown} />
            </span>
            <span className="select2-results">
              <ul className="select2-results__options" id={listboxId} role="listbox">
                { search.list.length > 0
                  ?
                  <>
                    { search.list.map (( (item, index) => { return (
                      <li key={index} id={getOptionId(index)} className={`select2-results__option select2-results__option--selectable ${countryCode(lang.code, item) === country.value ? "select2-results__option--selected" : ""} ${index === highlightedIndex ? "select2-results__option--highlighted" : ""}`} role="option" aria-selected={countryCode(lang.code, item) === country.value}>
                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => { handleChangeCountry(countryCode(lang.code, item)); handleClose(); }}>{item}</Dropdown.Item>
                      </li>
                    )}))}
                  </>
                  :
                  <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      <AppLegend component={component} attribute={{check: country.valid, name: "country", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
    </>
  )
}

export default AppInputCountry;
